import { QueryClient } from '@tanstack/react-query';
import { sdkLogger } from './logger/logger';
import { toSdkLoggableError } from './types/shared';

export const sdkQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,          // Keep the survey layout cached forever during the session
      refetchOnWindowFocus: false,  // Prevent layout flashes when switching browser tabs
      refetchOnReconnect: false,    // Avoid redundant network calls on internet reconnection
      refetchInterval: false,       // Do not poll (survey metadata is static)
      retryDelay: 5000, // 5 seconds delay between retries
      retry: (failureCount, error) => {
        // Stop retrying after 3 attempts
        if (failureCount >= 3) {
          sdkLogger.error(`Query failed permanently after 3 attempts. Stopping retries.`, { err: toSdkLoggableError(error) });
          return false;
        }

        // Do not retry on permanent client errors (400, 401, 403, 404, 422)
        if (error instanceof Error) {
          const msg = error.message;
          if (
            msg.includes('400') || // 400 Bad Request
            msg.includes('401') || // 401 Unauthorized
            msg.includes('403') || // 403 Forbidden
            msg.includes('404') || // 404 Not Found
            msg.includes('422')    // 422 Unprocessable Entity
          ) {
            sdkLogger.error(`Query failed permanently due to client error (${msg}). Skipping retry.`, { err: toSdkLoggableError(error) });
            return false;
          }
        }

        sdkLogger.warn(`Query failed. Retrying attempt ${failureCount + 1}/3...`, { err: toSdkLoggableError(error) });
        return true;
      },
    },
    mutations: {
      networkMode: 'always', // Prevent React Query from pausing mutations when offline, forcing immediate retry logging
      retryDelay: 5000, // 5 seconds delay between retries
      retry: (failureCount, error) => {
        // Retry up to 3 times for transient network/server failures
        if (failureCount >= 3) {
          sdkLogger.error(`Mutation failed permanently after 3 attempts. Stopping retries.`, { err: toSdkLoggableError(error) });
          return false;
        }

        // Do not retry on permanent validation or client errors (400, 401, 403, 422)
        if (error instanceof Error) {
          const msg = error.message;
          if (
            msg.includes('400') || // 400 Bad Request (Client syntax / invalid parameters)
            msg.includes('401') || // 401 Unauthorized (Authentication issues)
            msg.includes('403') || // 403 Forbidden (Insufficient permissions)
            msg.includes('422') // 422 Unprocessable Entity (Semantic validation failure)
          ) {
            sdkLogger.error(`Mutation failed permanently due to validation/client error. Skipping retry.`, { err: toSdkLoggableError(error) });
            return false;
          }
        }

        sdkLogger.warn(`Mutation failed. Retrying attempt ${failureCount + 1}/3...`, { err: toSdkLoggableError(error) });
        return true;
      },
    },
  },
});
